import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import type { useOcsrImport } from '../../hooks/useOcsrImport';

interface OcsrImportPanelProps {
  readonly ocsrImport: ReturnType<typeof useOcsrImport>;
}

export function OcsrImportPanel({ ocsrImport }: OcsrImportPanelProps) {
  const { t } = useTranslation();
  const { selectedFile, updateSelectedFile, handDrawn, setHandDrawn, ocsrRecognitionState, handleOcsrImport } =
    ocsrImport;
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [pasteError, setPasteError] = useState<'noImage' | 'denied' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clearing a completed/reset upload also resets the native picker, including same-file picks.
  useEffect(() => {
    if (!selectedFile && fileInputRef.current) fileInputRef.current.value = '';
  }, [selectedFile]);

  // #981 -- a pasted image replaces whatever was picked; the file field is cleared so it never
  // shows a stale name next to the pasted one.
  const takePastedImage = useCallback(
    (image: Blob) => {
      const type = image.type || 'image/png';
      const file = new File([image], `pasted-image.${type.split('/')[1] ?? 'png'}`, { type });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPasteError(null);
      updateSelectedFile(file);
    },
    [updateSelectedFile]
  );

  // ⌘V / Ctrl+V anywhere in the open File drawer takes an image from the clipboard -- unless the
  // press is in a text field, which keeps its own paste.
  useEffect(() => {
    const paste = (event: ClipboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLInputElement && target.type !== 'file') ||
        (target instanceof HTMLElement && target.isContentEditable)
      )
        return;
      const image = [...(event.clipboardData?.files ?? [])].find((file) => file.type.startsWith('image/'));
      if (!image) return;
      event.preventDefault();
      takePastedImage(image);
    };
    document.addEventListener('paste', paste);
    return () => document.removeEventListener('paste', paste);
  }, [takePastedImage]);

  // The Paste button reads the clipboard directly (touch screens have no ⌘V). The browser may ask
  // for permission, or not offer clipboard reading at all.
  const pasteFromClipboard = async () => {
    try {
      for (const item of await navigator.clipboard.read()) {
        const type = item.types.find((candidate) => candidate.startsWith('image/'));
        if (type) {
          takePastedImage(await item.getType(type));
          return;
        }
      }
      setPasteError('noImage');
    } catch {
      setPasteError('denied');
    }
  };

  // A preview of the chosen image, so a paste visibly lands.
  const previewUrl = useMemo(() => (selectedFile ? URL.createObjectURL(selectedFile) : null), [selectedFile]);
  useEffect(() => (previewUrl ? () => URL.revokeObjectURL(previewUrl) : undefined), [previewUrl]);

  return (
    <section className="ocsr-import-panel" aria-labelledby="ocsr-import-heading">
      <div>
        <div className="ocsr-heading-row">
          <h2 id="ocsr-import-heading">{t('ocsrImportPanel.heading')}</h2>
          {/* #878: a tap-to-toggle disclosure rather than a hover tooltip, so it works on touch. */}
          <button
            className="icon-button ocsr-limits-toggle"
            type="button"
            aria-label={t('ocsrImportPanel.limits.toggle')}
            title={t('ocsrImportPanel.limits.toggle')}
            aria-expanded={limitsOpen}
            aria-controls="ocsr-limits"
            onClick={() => setLimitsOpen((open) => !open)}
          >
            <Icon name="info" size="1.1rem" />
          </button>
        </div>
        <p>{t('ocsrImportPanel.subheading')}</p>
        <p className="ocsr-review-notice">{t('ocsrImportPanel.limits.accuracy')}</p>
        <p>{t('ocsrImportPanel.pasteHint')}</p>
        {limitsOpen ? (
          <ul id="ocsr-limits" className="ocsr-limits">
            <li>{t('ocsrImportPanel.limits.bestWith')}</li>
            <li>{t('ocsrImportPanel.limits.handDrawn')}</li>
            <li>{t('ocsrImportPanel.limits.duration')}</li>
          </ul>
        ) : null}
      </div>
      <form onSubmit={(event) => void handleOcsrImport(event)}>
        <label htmlFor="ocsr-file-input">{t('ocsrImportPanel.label')}</label>
        <input
          ref={fileInputRef}
          id="ocsr-file-input"
          type="file"
          accept="image/*"
          onChange={(event) => {
            setPasteError(null);
            updateSelectedFile(event.target.files?.[0] ?? null);
          }}
        />
        <button
          className="icon-button"
          type="button"
          aria-label={t('ocsrImportPanel.pasteButton')}
          title={t('ocsrImportPanel.pasteButton')}
          disabled={ocsrRecognitionState.state === 'loading'}
          onClick={() => void pasteFromClipboard()}
        >
          <Icon name="paste" />
        </button>
        <button
          className={ocsrRecognitionState.state === 'loading' ? '' : 'icon-button'}
          type="submit"
          aria-label={t('ocsrImportPanel.importButton')}
          title={t('ocsrImportPanel.importButton')}
          disabled={ocsrRecognitionState.state === 'loading' || !selectedFile}
        >
          {ocsrRecognitionState.state === 'loading' ? t('ocsrImportPanel.recognizing') : <Icon name="arrowForward" />}
        </button>
      </form>
      {pasteError ? (
        <p className="import-error" role="alert">
          {t(`ocsrImportPanel.pasteErrors.${pasteError}`)}
        </p>
      ) : null}
      {previewUrl && selectedFile ? (
        <figure className="ocsr-preview">
          <img src={previewUrl} alt={t('ocsrImportPanel.previewAlt')} />
          <figcaption>{selectedFile.name}</figcaption>
        </figure>
      ) : null}
      <label className="ocsr-hand-drawn-toggle">
        <input
          type="checkbox"
          disabled={ocsrRecognitionState.state === 'loading'}
          checked={handDrawn}
          onChange={(event) => setHandDrawn(event.target.checked)}
        />
        <span>{t('ocsrImportPanel.handDrawnToggle')}</span>
      </label>
      {ocsrRecognitionState.state === 'loading' ? (
        <p className="ocsr-progress" role="status">
          {t('ocsrImportPanel.recognizingHint')}
        </p>
      ) : null}
      {ocsrRecognitionState.state === 'imported' ? (
        <>
          <p className="import-success" role="status">
            {t('ocsrImportPanel.importedAs', { canonicalSmiles: ocsrRecognitionState.canonicalSmiles })}
          </p>
          <p className="ocsr-review-notice" role="status">
            <Icon name="info" size="1.1rem" />
            <span>{t('ocsrImportPanel.reviewNotice')}</span>
          </p>
        </>
      ) : null}
      {ocsrRecognitionState.state === 'error' ? (
        <p className="import-error" role="alert">
          {t(`ocsrImportPanel.errors.${ocsrRecognitionState.kind}`)}
        </p>
      ) : null}
    </section>
  );
}
